import { loadStripe } from '@stripe/stripe-js';
import pick from 'lodash/pick';

import { CHECKOUT_TYPE, ORIGIN_CONTEXT } from '../../util/checkoutContext';
import { isCordova } from '../../util/mobile/deviceDetection';

import { trackBookingRequest, trackInitPurchase } from '../../analytics/conversions';
import config from '../../config';
import { collectTrackingContext } from '../../util/consent/trackingContext';
import { fetchTranslations } from '../../ducks/translations.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  initiatePrivileged,
  subscriptionCreateSession,
  subscriptionFulfill,
  subscriptionPassPrices,
  subscriptionUpgradeCheckout,
  subscriptionUpgradeFulfill,
  transitionPrivileged,
  vansiteCapturePaymentIntent,
  vansiteConfirmPaymentIntent,
  vansiteCreatePaymentIntent,
} from '../../util/api';
import { denormalisedEntities, denormalisedResponseEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import {
  EXPRESS_BOOKING,
  isPrivileged as isPrivilegedDefault,
  TRANSITION_AUTO_CONFIRM_PAYMENT,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_REQUEST_PAYMENT,
} from '../../util/transaction.default';
import { showListing } from '../ListingPage/ListingPage.duck';

// ================ Action types ================ //

export const SET_INITIAL_VALUES_REQUEST = 'app/CheckoutPage/SET_INITIAL_VALUES_REQUEST';
export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const CREATE_PAYMENT_INTENT_REQUEST = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_REQUEST';
export const CREATE_PAYMENT_INTENT_SUCCESS = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_SUCCESS';
export const CREATE_PAYMENT_INTENT_ERROR = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_ERROR';

export const CAPTURE_PAYMENT_INTENT_REQUEST = 'app/CheckoutPage/CAPTURE_PAYMENT_INTENT_REQUEST';
export const CAPTURE_PAYMENT_INTENT_SUCCESS = 'app/CheckoutPage/CAPTURE_PAYMENT_INTENT_SUCCESS';
export const CAPTURE_PAYMENT_INTENT_ERROR = 'app/CheckoutPage/CAPTURE_PAYMENT_INTENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/ListingPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_PREVIEW_REQUEST =
  'app/ListingPage/SPECULATE_TRANSACTION_PREVIEW_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/ListingPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/ListingPage/SPECULATE_TRANSACTION_ERROR';

export const SET_UPSELL_PASS = 'app/CheckoutPage/SET_UPSELL_PASS';
export const SET_PASS_PRICES = 'app/CheckoutPage/SET_PASS_PRICES';
export const PASS_UPSELL_REQUEST = 'app/CheckoutPage/PASS_UPSELL_REQUEST';
export const PASS_UPSELL_SUCCESS = 'app/CheckoutPage/PASS_UPSELL_SUCCESS';
export const PASS_UPSELL_ERROR = 'app/CheckoutPage/PASS_UPSELL_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.CheckoutPage} */
const initialState = {
  listing: null,
  bookingData: null,
  bookingDates: null,
  speculateTransactionInProgress: false,
  // Quiet re-speculate used by the pass-upsell toggle: updates the breakdown in
  // place WITHOUT the full-page spinner that speculateTransactionInProgress drives.
  passPreviewInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  setInitialValuesInProgress: false,
  setInitialValues: null,
  upsellPassSelected: null,
  passUpsellInProgress: false,
  passUpsellError: null,
  // Live Stripe pass prices keyed by pass type ({ annualPass, annualPassFamily,
  // '25Pass' } → { amount, currency } | null), fetched per locale on the checkout.
  // null until fetched; the upsell/upgrade cards fall back to display constants.
  passPrices: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES_REQUEST:
      return { ...state, setInitialValues: payload, setInitialValuesInProgress: true };

    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload, setInitialValuesInProgress: false };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
      };
    case SPECULATE_TRANSACTION_PREVIEW_REQUEST:
      // Quiet variant: do NOT raise speculateTransactionInProgress (which would
      // blank the breakdown + disable the form). The breakdown updates in place
      // once SUCCESS arrives; passPreviewInProgress only drives a subtle dim.
      return {
        ...state,
        passPreviewInProgress: true,
        speculateTransactionError: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        passPreviewInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      return {
        ...state,
        speculateTransactionInProgress: false,
        passPreviewInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      return { ...state, confirmPaymentError: payload };

    case CREATE_PAYMENT_INTENT_REQUEST:
      return { ...state, paymentIntentError: null };
    case CREATE_PAYMENT_INTENT_SUCCESS:
      return state;
    case CREATE_PAYMENT_INTENT_ERROR:
      return { ...state, paymentIntentError: payload };

    case CAPTURE_PAYMENT_INTENT_REQUEST:
      return { ...state, paymentIntentError: null };
    case CAPTURE_PAYMENT_INTENT_SUCCESS:
      return state;
    case CAPTURE_PAYMENT_INTENT_ERROR:
      return { ...state, paymentIntentError: payload };

    case SET_UPSELL_PASS:
      return {
        ...state,
        upsellPassSelected: payload,
        passUpsellError: null,
      };
    case SET_PASS_PRICES:
      return { ...state, passPrices: payload };
    case PASS_UPSELL_REQUEST:
      return {
        ...state,
        passUpsellInProgress: true,
        passUpsellError: null,
      };
    case PASS_UPSELL_SUCCESS:
      return { ...state, passUpsellInProgress: false };
    case PASS_UPSELL_ERROR:
      return { ...state, passUpsellInProgress: false, passUpsellError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

const isPrivileged = (transition) => {
  return isPrivilegedDefault(transition);
};

// ================ Action creators ================ //

export const setInitialValuesRequest = (params) => ({
  type: SET_INITIAL_VALUES_REQUEST,
  payload: params,
});

export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = (order) => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = (e) => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = (orderId) => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = (e) => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

const createPaymentIntentRequest = () => ({
  type: CREATE_PAYMENT_INTENT_REQUEST,
});

const createPaymentIntentSuccess = (order) => ({
  type: CREATE_PAYMENT_INTENT_SUCCESS,
  payload: order,
});

const createPaymentIntentError = (e) => ({
  type: CREATE_PAYMENT_INTENT_ERROR,
  error: true,
  payload: e,
});

const capturePaymentIntentRequest = () => ({
  type: CAPTURE_PAYMENT_INTENT_REQUEST,
});

const capturePaymentIntentSuccess = (order) => ({
  type: CAPTURE_PAYMENT_INTENT_SUCCESS,
  payload: order,
});

const capturePaymentIntentError = (e) => ({
  type: CAPTURE_PAYMENT_INTENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionPreviewRequest = () => ({
  type: SPECULATE_TRANSACTION_PREVIEW_REQUEST,
});

export const speculateTransactionSuccess = (transaction) => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = (e) => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const setUpsellPass = (passType) => ({ type: SET_UPSELL_PASS, payload: passType });
export const setPassPrices = (prices) => ({ type: SET_PASS_PRICES, payload: prices });
const passUpsellRequest = () => ({ type: PASS_UPSELL_REQUEST });
const passUpsellSuccess = () => ({ type: PASS_UPSELL_SUCCESS });
export const passUpsellError = (message) => ({
  type: PASS_UPSELL_ERROR,
  error: true,
  payload: message,
});

/**
 * Create the hosted Stripe Checkout Session for the selected upsell pass and
 * return its URL (the caller redirects via openStripeHosted). `voucherCodes` is
 * an array of plain code strings the customer redeemed in the upsell voucher
 * modal — the backend (checkout-upsell path) applies them to the subscription.
 * On the 409 "already active" case the server returns plain JSON
 * `{ error: { code } }` — surface the code through passUpsellError so the
 * reconcile UI can react.
 *
 * @returns {import('../../../types/vansite').AppThunkAction<string|undefined>}
 */
export const createUpsellSubscriptionSession =
  ({ passType, locale, returnUrl, lableText, voucherCodes = [] }) =>
  async (dispatch) => {
    dispatch(passUpsellRequest());
    try {
      // Hosted Checkout returns into the booking checkout, not onto a thank-you
      // page — the purchase is fired server-side from the Stripe webhook, which
      // needs this consent context stored under the session id.
      const tracking = await collectTrackingContext();
      const response = await subscriptionCreateSession({
        passType,
        voucherCodes,
        successUrl: returnUrl,
        returnUrl,
        originContext: isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB,
        checkoutType: CHECKOUT_TYPE.UPSELL,
        passActivation: false,
        lableText,
        locale,
        tracking,
      });
      return response?.data?.url;
    } catch (e) {
      logError(e, 'checkout-upsell-create-session');
      const code = e?.error?.code || 'pass-upsell-failed';
      dispatch(passUpsellError(code));
      return undefined;
    }
  };

/**
 * Return-from-Stripe handler: fulfill the session (links the sub to the profile),
 * then refetch the current user so `hasCurrentUserValidAnnualPass` flips true.
 * Returns true on success. The caller then re-speculates the booking.
 *
 * @returns {import('../../../types/vansite').AppThunkAction<boolean|undefined>}
 */
export const fulfillPassCheckout = (sessionId) => async (dispatch) => {
  dispatch(passUpsellRequest());
  try {
    const result = await subscriptionFulfill({ sessionId });
    await dispatch(fetchCurrentUser());
    dispatch(passUpsellSuccess());
    return !!result?.data?.success;
  } catch (e) {
    logError(e, 'checkout-pass-fulfill');
    dispatch(passUpsellError('pass-fulfill-failed'));
    return undefined;
  }
};

/**
 * Create the hosted Checkout Session for the pass UPGRADE (code-pass holder →
 * subscription, −30 €). Mirrors createUpsellSubscriptionSession but hits the
 * dedicated upgrade endpoint: no voucherCodes, no licence-plate label — the
 * server derives everything from the profile pass.
 *
 * @returns {import('../../../types/vansite').AppThunkAction<string|undefined>}
 */
export const createPassUpgradeSession =
  ({ passType, locale, returnUrl }) =>
  async (dispatch) => {
    dispatch(passUpsellRequest());
    try {
      const tracking = await collectTrackingContext();
      const response = await subscriptionUpgradeCheckout({
        passType,
        successUrl: returnUrl,
        returnUrl,
        locale,
        originContext: isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB,
        tracking,
      });
      return response?.data?.url;
    } catch (e) {
      logError(e, 'checkout-pass-upgrade-create-session');
      dispatch(passUpsellError(e?.error?.code || 'pass-upgrade-failed'));
      return undefined;
    }
  };

/**
 * Return-from-Stripe handler for the upgrade: fulfill the session (links the new
 * subscription to the profile), then refetch the user so the upgrade card
 * disappears (subscriptionId is now set). NO re-speculate — the booking keeps
 * the old pass conditions until the subscription's billing anchor (pass-end).
 *
 * @returns {import('../../../types/vansite').AppThunkAction<boolean|undefined>}
 */
export const fulfillPassUpgrade = (sessionId) => async (dispatch) => {
  dispatch(passUpsellRequest());
  try {
    const result = await subscriptionUpgradeFulfill({ sessionId });
    await dispatch(fetchCurrentUser());
    dispatch(passUpsellSuccess());
    return !!result?.data?.success;
  } catch (e) {
    logError(e, 'checkout-pass-upgrade-fulfill');
    dispatch(passUpsellError('pass-fulfill-failed'));
    return undefined;
  }
};

/**
 * Fetch the live Stripe pass prices for a locale and stash them in state so the
 * checkout upsell/upgrade cards show the real price (and the right per-language
 * product) instead of a stale display constant. Best-effort: on failure the
 * cards keep their hardcoded fallback, so this never blocks the checkout.
 *
 * @returns {import('../../../types/vansite').AppThunkAction<void>}
 */
export const fetchPassPrices = (locale) => async (dispatch) => {
  try {
    const response = await subscriptionPassPrices({ locale });
    if (response?.data) dispatch(setPassPrices(response.data));
  } catch (e) {
    // Non-fatal: the cards fall back to their display constants.
    logError(e, 'checkout-pass-prices-fetch');
  }
};

/* ================ Thunks ================ */

export const initiateOrder =
  (orderParams, transactionId, processAlias) => (dispatch, getState, sdk) => {
    dispatch(initiateOrderRequest());

    const { currentUser } = getState().user;
    const voucherifyCustomer = currentUser?.attributes?.profile?.privateData?.voucherifyCustomer;
    // If we already have a transaction ID, we should transition, not
    // initiate.
    const isTransition = !!transactionId;
    const transition = isTransition ? null : TRANSITION_REQUEST_PAYMENT;
    const isPrivilegedTransition = isPrivileged(transition);

    const { bookingData: orderBookingData, ...otherFields } = orderParams;

    const bookingData = {
      startDate: orderParams.bookingStart,
      endDate: orderParams.bookingEnd,
      ...orderBookingData,
    };

    const bodyParams = isTransition
      ? {
        id: transactionId,
        transition,
        params: otherFields,
      }
      : {
        processAlias,
        transition,
        params: otherFields,
      };

    const queryParams = {
      include: ['booking', 'provider', 'listing'],
      expand: true,
    };

    const handleSuccess = (response) => {
      const entities = denormalisedResponseEntities(response);
      const order = entities[0];
      dispatch(initiateOrderSuccess(order));
      return order;
    };

    const handleError = (e) => {
      dispatch(initiateOrderError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      logError(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
        listingId: orderParams.listingId.uuid,
        bookingStart: orderParams.bookingStart,
        bookingEnd: orderParams.bookingEnd,
      });
      throw e;
    };

    if (isTransition && isPrivilegedTransition) {
      // transition privileged
      return transitionPrivileged({
        isSpeculative: false,
        bookingData,
        bodyParams,
        queryParams,
      })
        .then(handleSuccess)
        .catch(handleError);
    } else if (isTransition) {
      // transition non-privileged
      return sdk.transactions
        .transition(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    } else if (isPrivilegedTransition) {
      // initiate privileged
      return initiatePrivileged({
        isSpeculative: false,
        bookingData,
        bodyParams,
        queryParams,
        voucherifyCustomer,
      })
        .then(handleSuccess)
        .catch(handleError);
    } else {
      // initiate non-privileged
      return sdk.transactions
        .initiate(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    }
  };

export const createPaymentIntent = (params) => (dispatch) => {
  dispatch(createPaymentIntentRequest());
  return vansiteCreatePaymentIntent({ params })
    .then((response) => {
      const order = response.data.data;
      dispatch(createPaymentIntentSuccess(order));
      return order;
    })
    .catch((e) => {
      dispatch(createPaymentIntentError(storableError(e)));
      logError(e, 'stripe-create-payment-intent-failed');
      throw e;
    });
};

export const confirmPayment = (transaction, category, reportAddress) => async (dispatch, getState) => {
  let { currentUser } = getState().user;
  // Redirected payment flows can resume before Redux has rehydrated the user
  // (e.g. Cordova app returning from Stripe/PayPal). Refetch before reading
  // voucherifyCustomer — every user is expected to have one, and falling back
  // to anonymous would create orphan Voucherify customers.
  if (!currentUser) {
    await dispatch(fetchCurrentUser());
    currentUser = getState().user.currentUser;
  }
  if (!currentUser) {
    throw new Error('confirmPayment requires an authenticated currentUser');
  }

  dispatch(confirmPaymentRequest());
  let providerNotification = undefined;
  if (category === EXPRESS_BOOKING) {
    providerNotification = false;
  }

  const bodyParams = {
    id: transaction.id.uuid,
    transition:
      category === EXPRESS_BOOKING ? TRANSITION_AUTO_CONFIRM_PAYMENT : TRANSITION_CONFIRM_PAYMENT,
    params: {
      metadata: {
        providerNotification,
        reportAddress,
      },
    },
  };
  // The transition response does not include the listing, so the title has to come
  // from the page state that seeded the checkout.
  const listingTitle = getState().CheckoutPage?.listing?.attributes?.title;
  const tracking = await collectTrackingContext({ listingTitle });
  const params = {
    voucherifyCustomer: currentUser.attributes.profile?.privateData?.voucherifyCustomer,
    currentUserId: currentUser.id.uuid,
    tracking,
  };

  return vansiteConfirmPaymentIntent({ bodyParams, params })
    .then((response) => {
      const transaction = response.data.data;
      // Purchase feuert nicht mehr bei Preauth (Spec W1): Express-Purchase
      // kommt clientseitig auf der OrderDetailsPage (fromCheckout-Signal),
      // der Anfrage-Flow bekommt stattdessen das booking_request-Signal —
      // der Server-Purchase bei Capture übernimmt die Conversion.
      if (category !== EXPRESS_BOOKING) {
        trackBookingRequest(transaction, currentUser, listingTitle);
      }
      dispatch(confirmPaymentSuccess(transaction.id));
      return transaction;
    })
    .catch((e) => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transaction.id ? { transactionId: transaction.id.uuid } : {};
      logError(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const capturePayment = (params) => (dispatch) => {
  const transactionId = params.id.uuid;

  dispatch(capturePaymentIntentRequest());
  return vansiteCapturePaymentIntent({ transactionId })
    .then((response) => {
      const order = response.data.data;
      dispatch(capturePaymentIntentSuccess(order));
      return order;
    })
    .catch((e) => {
      dispatch(capturePaymentIntentError(storableError(e)));
      logError(e, 'stripe-capture-payment-intent-failed');
      throw e;
    });
};

export const sendMessage = (params) => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch((e) => {
        logError(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({
      orderId,
      messageSuccess: true,
      transaction: params.transaction,
      ...params.setupIntent,
    });
  }
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction =
  (orderParams, transactionId, locale, { preview = false } = {}) =>
  (dispatch, getState, sdk) => {
    dispatch(preview ? speculateTransactionPreviewRequest() : speculateTransactionRequest());

    const { currentUser } = getState().user;
    const voucherifyCustomer = currentUser?.attributes?.profile?.privateData?.voucherifyCustomer;

    // If we already have a transaction ID, we should transition, not
    // initiate.
    const isTransition = !!transactionId;
    const transition = isTransition ? null : TRANSITION_REQUEST_PAYMENT;
    const isPrivilegedTransition = isPrivileged(transition);

    const { bookingData: orderBookingData, ...otherFields } = orderParams;

    const bookingData = {
      startDate: orderParams.bookingStart,
      endDate: orderParams.bookingEnd,
      ...orderBookingData,
    };

    const params = {
      ...otherFields,
      cardToken: 'CheckoutPage_speculative_card_token',
    };

    const bodyParams = isTransition
      ? {
        id: transactionId,
        transition,
        params,
      }
      : {
        processAlias: config.bookingProcessAlias,
        transition,
        params,
      };

    const queryParams = {
      include: ['booking', 'provider'],
      expand: true,
    };

    const handleSuccess = (response) => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the speculate response');
      }
      const tx = entities[0];
      dispatch(speculateTransactionSuccess(tx));
      // The speculate response only includes booking/provider — take the title
      // from the page state instead.
      trackInitPurchase(
        tx,
        orderParams,
        currentUser,
        getState().CheckoutPage?.listing?.attributes?.title,
      );
    };

    const handleError = (e) => {
      const { listingId, bookingStart, bookingEnd } = params;
      logError(e, 'speculate-transaction-failed', {
        listingId: listingId.uuid,
        bookingStart,
        bookingEnd,
      });
      return dispatch(speculateTransactionError(storableError(e)));
    };

    dispatch(showListing(orderParams.listingId, locale));
    dispatch(fetchTranslations({ listing: { ids: [orderParams.listingId?.uuid] } }, locale));

    if (isTransition && isPrivilegedTransition) {
      // transition privileged
      return transitionPrivileged({
        isSpeculative: true,
        bookingData,
        bodyParams,
        queryParams,
      })
        .then(handleSuccess)
        .catch(handleError);
    } else if (isTransition) {
      // transition non-privileged
      return sdk.transactions
        .transitionSpeculative(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    } else if (isPrivilegedTransition) {
      // initiate privileged
      return initiatePrivileged({
        isSpeculative: true,
        bookingData,
        bodyParams,
        queryParams,
        voucherifyCustomer,
      })
        .then(handleSuccess)
        .catch(handleError);
    } else {
      // initiate non-privileged
      return sdk.transactions
        .initiateSpeculative(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    }
  };

export const fetchCheckoutTransaction = (locale) => async (dispatch, getState, sdk) => {
  const {
    transactionId,
    listingId,
    paymentIntentId,
    paymentIntentClientSecret,
    paymentFlowType,
    message,
    saveAfterOnetimePayment,
    paymentMethodSaved,
  } = getState().CheckoutPage.setInitialValues;

  const stripe = await loadStripe(config.stripe.publishableKey || '');
  const translationQuery = { listing: { ids: [listingId?.uuid] } };

  const [transactionsResponse, listingsResponse, paymentIntentResponse] = await Promise.all([
    sdk.transactions.show(
      {
        id: transactionId,
        include: ['provider', 'booking'],
      },
      { expand: true },
    ),
    sdk.listings.show(
      {
        id: listingId,
        include: ['author', 'author.profileImage', 'images'],
        'fields.image': [
          'variants.landscape-crop',
          'variants.landscape-crop2x',
          'variants.landscape-crop4x',
          'variants.landscape-crop6x',
          'variants.square-small',
          'variants.square-small2x',
        ],
      },
      { expand: true },
    ),
    stripe?.retrievePaymentIntent(paymentIntentClientSecret),
    dispatch(fetchTranslations(translationQuery, locale)),
  ]);

  let entities = updatedEntities({}, transactionsResponse.data);
  entities = updatedEntities(entities, listingsResponse.data);
  const listingRef = { id: { uuid: listingId }, type: 'listing' };
  const transactionRef = { id: { uuid: transactionId }, type: 'transaction' };
  const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
  const listing = denormalised[0];
  const transaction = denormalised[1];

  const transactionPaymentIntentId =
    transaction.attributes.protectedData.stripePaymentIntents?.default.stripePaymentIntentId;
  const transactionPaymentIntentClientSecret =
    transaction.attributes.protectedData.stripePaymentIntents?.default
      .stripePaymentIntentClientSecret;

  if (
    transactionPaymentIntentId === paymentIntentId &&
    transactionPaymentIntentClientSecret === paymentIntentClientSecret
  ) {
    const { metadata } = transaction.attributes;
    const { cancellationPolicy, guestCount, voucher, travelDetails, annualPassVoucher } = metadata;

    return {
      paymentFlowType,
      message,
      paymentIntent: paymentIntentResponse?.paymentIntent,
      pageData: {
        listing,
        transaction,
        bookingData: {
          annualPassVoucher,
          cancellationPolicy: cancellationPolicy,
          guestCount,
          voucher,
        },
        bookingDates: {
          bookingStart: transaction.booking.attributes.start,
          bookingEnd: transaction.booking.attributes.end,
        },
      },
      travelDetails,
      saveAfterOnetimePayment,
      paymentMethodSaved,
    };
  }
};

export const loadData = (params, search, match) => async (dispatch, getState, sdk) => {
  // PayPal returns a url with search params not compatible with react hash router
  const routerSearchParams = new URLSearchParams(search);
  // In Cordova hash-based routing (app://host/index.html#/path?params),
  // query params are inside the fragment, so window.location.search is empty.
  const windowRawSearch =
    typeof window !== 'undefined'
      ? window.location.search ||
        (window.location.hash.includes('?')
          ? '?' + window.location.hash.split('?').slice(1).join('?')
          : '')
      : '';
  const windowSearchParams = new URLSearchParams(windowRawSearch);
  const allSearchParams = new URLSearchParams();
  for (const [key, value] of routerSearchParams) {
    allSearchParams.set(key, value);
  }
  for (const [key, value] of windowSearchParams) {
    allSearchParams.set(key, value);
  }

  const listingId = params.id;
  const transactionId = allSearchParams.get('orderId');
  const paymentIntentId = allSearchParams.get('payment_intent');
  const paymentIntentClientSecret = allSearchParams.get('payment_intent_client_secret');
  const redirectStatus = allSearchParams.get('redirect_status');

  if (transactionId && paymentIntentId && paymentIntentClientSecret) {
    const paymentFlowType = allSearchParams.get('paymentFlowType');

    // Read message from sessionStorage (stored before PayPal redirect to avoid URL length limits)
    let message = null;
    let saveAfterOnetimePayment = false;
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const stored = window.sessionStorage.getItem(`paypal_return_${transactionId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          message = parsed.message || null;
          saveAfterOnetimePayment = parsed.saveAfterOnetimePayment === true;
          window.sessionStorage.removeItem(`paypal_return_${transactionId}`);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    // Backward compat: fall back to URL params for in-flight redirects
    if (!message) {
      const urlMessage = allSearchParams.get('message');
      if (urlMessage) {
        try { message = atob(urlMessage); } catch (e) { message = null; }
      }
    }
    if (!saveAfterOnetimePayment) {
      saveAfterOnetimePayment = allSearchParams.get('saveAfterOnetimePayment') === 'true';
    }

    if (['pending', 'succeeded'].includes(redirectStatus)) {
      dispatch(
        setInitialValuesRequest({
          listingId,
          transactionId,
          paymentIntentId,
          paymentIntentClientSecret,
          message,
          paymentFlowType,
          saveAfterOnetimePayment,
        }),
      );
    } else if (redirectStatus === 'failed') {
      dispatch(
        setInitialValuesRequest({
          listingId,
          transactionId,
          paymentIntentId,
          paymentIntentClientSecret,
          message,
          paymentFlowType,
          saveAfterOnetimePayment,
        }),
      );
    }
  }
};
